<template>
  <BaseWidget
    title="Automated workflows"
    :show-info-icon="true"
    info-tooltip="Overview of your custom automated workflows and their current status."
    :info-tooltip-link="{ url: 'https://support.optixapp.com/en/articles/9500741-what-is-an-automation', text: 'Learn about Automations' }"
    :automations-count="false"
    :disable-date-range="true"
    :edit-mode="editMode"
    :widget-id="props.widgetId"
    @remove-widget="$emit('remove-widget', props.widgetId)"
  >
    <div
      v-if="loading"
      class="loading-state"
    >
      <SkeletonLoader
        type="widget"
        :compact="focused"
      />
    </div>
    <div
      v-else-if="error"
      class="error-state"
    >
      <RateLimitError v-if="isRateLimitError(error)" />
      <span v-else>Error: {{ error.message }}</span>
    </div>
    <MetricValue 
      v-else
      :value="automationCount" 
      subtitle="Live automations" 
    />
  </BaseWidget>
</template>

<script setup>
import { computed, onUnmounted } from 'vue'
import BaseWidget from './BaseWidget.vue'
import MetricValue from './MetricValue.vue'
import SkeletonLoader from './SkeletonLoader.vue'
import RateLimitError from './RateLimitError.vue'
import { useCustomAutomationsData } from '../composables/useWidgetData.js'
import { isRateLimitError } from '../utils/errorUtils.js'

const props = defineProps({
  editMode: {
    type: Boolean,
    default: false
  },
  widgetId: {
    type: String,
    default: null
  },
  sectionId: {
    type: String,
    default: null
  },
  widgetType: {
    type: String,
    default: null
  },
  focused: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['remove-widget'])

const { data, loading, error, cleanup } = useCustomAutomationsData({
  priority: 1
})

const automationCount = computed(() => {
  if (!data.value?.workflows?.total) return '0'
  return data.value.workflows.total.toString()
})

onUnmounted(() => {
  cleanup()
})
</script>

<style scoped>
.loading-state,
.error-state {
  padding: 20px;
  text-align: center;
  color: rgba(0, 0, 0, 0.87);
  font-size: 14px;
}

.error-state {
  color: var(--color-red);
}

.tooltip-wrapper {
  display: inline-block;
  margin-left: 6px;
  position: relative;
}

.tooltip-icon {
  font-size: 16px;
  cursor: help;
  opacity: 0.6;
  display: inline-block;
  vertical-align: middle;
  color: rgba(0, 0, 0, 0.87);
}

.tooltip-wrapper:hover .tooltip-text {
  visibility: visible;
  opacity: 1;
}

.tooltip-text {
  visibility: hidden;
  opacity: 0;
  background-color: #333;
  color: #fff;
  text-align: center;
  border-radius: 4px;
  padding: 6px 8px;
  position: absolute;
  z-index: 10010 !important;
  bottom: 125%;
  left: 50%;
  transform: translateX(-50%);
  white-space: nowrap;
  font-size: 12px;
  transition: opacity 0.2s;
  pointer-events: none;
}

:deep(.setting-select:disabled) {
  color: rgba(0, 0, 0, 0.38);
  border-bottom-style: dotted;
  cursor: not-allowed;
  opacity: 0.6;
}
</style>
